import React, { useState, useEffect, useRef } from "react"
import Button from "@mui/material/Button"
import AttachmentIcon from "@mui/icons-material/Attachment"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import DeleteIcon from "@mui/icons-material/Delete"
import FileUploadIcon from "@mui/icons-material/FileUpload"
import AvTimerRoundedIcon from "@mui/icons-material/AvTimerRounded"
import UploadFileIcon from "@mui/icons-material/UploadFile"
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn"
import ErrorIcon from "@mui/icons-material/Error"
import TextField from "@mui/material/TextField"
import RichTextEditor from "components/common/RichTextEditor"
import Tooltip from "@mui/material/Tooltip"
import Alert from "@mui/material/Alert"
import Chip from "@mui/material/Chip"
import axios from "axios"

import { bytesToSize, secondToTime } from "utils/convert"
import {
  calculateVideoDuration,
  findMediaInfoByFileName,
  generateUniqueFileName,
} from "utils/media"
import {
  MEDIA_TYPE,
  MEDIA_TYPE_INDEX,
  EXT_TO_MEDIA_TYPE,
  EXT_TO_MEDIA_TYPE_COLOR,
} from "constants/global"
import { addResource } from "services/media"
import { httpPost } from "utils/httpRequest"
import CircularProgressWithLabel from "./CircularProgressWithLabel"
import {
  StyledFileUploadCard,
  StyledMediaPreview,
  StyledPaperDiv,
} from "./MediaManager.style"

const DIRECT_UPLOAD_THRESHOLD_BYTES = 512 * 1024 * 1024

const deriveTitleFromFileName = (fileName) => {
  if (!fileName || typeof fileName !== "string") return ""
  const baseName = fileName.split(/[\\/]/).pop() || ""
  const lastDot = baseName.lastIndexOf(".")
  return lastDot > 0 ? baseName.slice(0, lastDot) : baseName
}

const UploadItemCard = ({
  uploadFile,
  index,
  cmsConfig,
  uploadList,
  setUploadList,
  onSelect, // new prop for select callback
}) => {
  const {
    S3_PRIVATE_BUCKET,
    S3_PUBLIC_BUCKET,
    S3_UPLOAD_BUCKET,
    S3_REGION,
  } = cmsConfig

  const isS3ConfigLoaded = S3_PRIVATE_BUCKET && S3_PUBLIC_BUCKET && S3_UPLOAD_BUCKET

  const [name, setName] = useState(
    () => uploadFile?.uploadName || deriveTitleFromFileName(uploadFile?.file?.name)
  )
  const [description, setDescription] = useState(() => uploadFile?.description || "")
  const [progress, setProgress] = useState(0)
  const [uploading, setUploading] = useState(false)
  const [uploadSucceeded, setUploadSucceeded] = useState(false)
  const [uploadError, setUploadError] = useState("")
  const [validated, setValidated] = useState(false)
  const [duration, setDuration] = useState(0)
  const [show, setShow] = useState(true)

  const previewVideoRef = useRef(null)

  const mediaInfo = findMediaInfoByFileName(uploadFile.file.name)
  const isImage = mediaInfo.type === MEDIA_TYPE.IMAGE
  const isDescriptionRequired = !isImage
  const bucketMediaType = MEDIA_TYPE_INDEX[mediaInfo.type]

  useEffect(() => {
    calculateVideoDuration(uploadFile.file, setDuration)
  }, [uploadFile])

  useEffect(() => {
    const isValid =
      name.trim().length >= 3 &&
      (!isDescriptionRequired || description.trim().length >= 10)
    setValidated(isValid)
    // Update the validated property in the parent uploadList
    setUploadList((prevList) =>
      prevList.map((file) =>
        file.key === uploadFile.key ? { ...file, validated: isValid } : file
      )
    )
  }, [name, description, isDescriptionRequired, setUploadList, uploadFile.key])
  
  const removeUploadCardFromList = (key) => {
    setUploadList(prevList => prevList.filter(file =>
      file.key !== key
    ))
    setShow(false)
  }

  const getUploadButtonStatus = () => {
    if (!isS3ConfigLoaded) {
      return {
        disabled: true,
        tooltip: "Loading S3 configuration...",
        variant: "outlined"
      }
    }
    if (!validated) {
      const nameValid = name.trim().length >= 3
      const descriptionValid = !isDescriptionRequired || description.trim().length >= 10
      let tooltip = "Please fill in all required fields:\n"
      if (!nameValid) tooltip += "• Name (minimum 3 characters)\n"
      if (!descriptionValid) tooltip += "• Description (minimum 10 characters)"
      
      return {
        disabled: true,
        tooltip,
        variant: "outlined"
      }
    }
    return {
      disabled: false,
      tooltip: "Upload file",
      variant: "contained"
    }
  }

  const upload = async (file) => {
    setUploadError("")
    
    // Check if S3 configuration is loaded
    if (!S3_PRIVATE_BUCKET || !S3_PUBLIC_BUCKET || !S3_UPLOAD_BUCKET) {
      setUploadError("S3 configuration not loaded. Please try again.")
      return
    }

    // Validate required fields
    if (!validated) {
      setUploadError("Please fill in all required fields before uploading.")
      return
    }

    // file info
    const mediaInfo = findMediaInfoByFileName(file.file.name)
    // new key
    const awsFileName = generateUniqueFileName(file.file.name)
    const mediaType = EXT_TO_MEDIA_TYPE[mediaInfo.ext]
    const isMediaAsset =
      mediaInfo.type === MEDIA_TYPE.IMAGE ||
      mediaInfo.type === MEDIA_TYPE.VIDEO ||
      mediaInfo.type === MEDIA_TYPE.AUDIO
    const targetBucket = isMediaAsset ? S3_PUBLIC_BUCKET : S3_PRIVATE_BUCKET
    const key = mediaInfo.type === MEDIA_TYPE.IMAGE
      ? `image/${awsFileName}`
      : `${EXT_TO_MEDIA_TYPE[mediaInfo.ext]}/${awsFileName}`
    // Store the S3 key in the uploadFile object for later use
    file.s3Key = key

    // upload file to S3 bucket through backend
    setUploading(true)
    setProgress(0)
    
    try {
      const contentType = file.file.type || "application/octet-stream"
      const progressConfig = {
        onUploadProgress: (progressEvent) => {
          const total = progressEvent.total || file.file.size
          const percentCompleted = Math.round((progressEvent.loaded * 100) / total)
          setProgress(percentCompleted)
        }
      }

      if (file.file.size > DIRECT_UPLOAD_THRESHOLD_BYTES) {
        const signedUrlResponse = await httpPost("admin/systemSetting/getUploadUrl", {
          bucket: targetBucket,
          key,
          contentType,
          fileSize: file.file.size,
          mediaType: mediaInfo.type,
        })
        const uploadUrl = signedUrlResponse?.data?.data?.uploadUrl
        if (!uploadUrl) {
          throw new Error(
            signedUrlResponse?.data?.message || "Unable to prepare the upload."
          )
        }

        await axios.put(uploadUrl, file.file, {
          ...progressConfig,
          headers: {
            "Content-Type": contentType,
          },
        })
      } else {
        const formData = new FormData()
        formData.append("file", file.file)
        formData.append("bucket", targetBucket)
        formData.append("key", key)

        const uploadResponse = await httpPost(
          "admin/systemSetting/uploadFile",
          formData,
          progressConfig
        )
        if (!uploadResponse?.data?.data?.success) {
          throw new Error(
            uploadResponse?.data?.data?.error || "The media upload failed."
          )
        }
      }

      // create S3 media record into database
      await addResource({
        mediaName: name.trim(),
        mediaDuration: duration,
        mediaDescription: description.trim(),
        mediaType: bucketMediaType || 0,
        mediaAwsKey: key,
        mediaAwsBucket: targetBucket,
        mediaSize: uploadFile.file.size,
      })

      setUploading(false)
      setUploadSucceeded(true)
      setUploadError("")
    } catch (err) {
      console.error("Upload error:", err)
      setUploading(false)
      setUploadSucceeded(false)
      const responseMessage = err.response?.data?.message
      setUploadError(
        (Array.isArray(responseMessage) ? responseMessage.join(" ") : responseMessage) ||
          err.message ||
          "Upload failed. Please try again."
      )
    }
  }

  const buttonStatus = getUploadButtonStatus()

  return show ? (
    <StyledFileUploadCard className={uploadSucceeded ? "upload-completed" : ""}>
      <div className="upload-property title truncated">
        <UploadFileIcon />
        <Tooltip title={uploadFile.file.name}>
          <span>{uploadFile.file.name}</span>
        </Tooltip>
      </div>
      
      <div className="upload-property">
        <AttachmentIcon />
        <Chip 
          label={mediaInfo.type} 
          size="small" 
          color="primary" 
          variant="outlined"
          sx={{ marginRight: 1 }}
        />
        <span>{bytesToSize(uploadFile.file.size)}</span>

        {(mediaInfo.type === MEDIA_TYPE.VIDEO ||
          mediaInfo.type === MEDIA_TYPE.AUDIO) && (
          <>
            <AvTimerRoundedIcon sx={{ marginLeft: 1 }} />
            <span>{secondToTime(duration)}</span>
          </>
        )}
      </div>
      
      <StyledMediaPreview>
        {(mediaInfo.type === MEDIA_TYPE.VIDEO ||
          mediaInfo.type === MEDIA_TYPE.AUDIO) && (
          <video
            controls
            id={`preview-video-${index}`}
            ref={(node) => {
              previewVideoRef.current = node
            }}
          >
            <source src={URL.createObjectURL(uploadFile.file)} />
          </video>
        )}

        {mediaInfo.type === MEDIA_TYPE.DOCUMENT && (
          <StyledPaperDiv color={EXT_TO_MEDIA_TYPE_COLOR[mediaInfo.ext]}>
            {mediaInfo.ext}
          </StyledPaperDiv>
        )}
        {mediaInfo.type === MEDIA_TYPE.IMAGE && (
          <img src={URL.createObjectURL(uploadFile.file)} alt="upload-preview" />
        )}
      </StyledMediaPreview>

      <div className="upload-property">
        <TextField
          label="Name *"
          variant="outlined"
          size="small"
          value={name}
          onChange={(e) => setName(e.target.value)}
          fullWidth
          error={name.length > 0 && name.trim().length < 3}
          helperText={name.length > 0 && name.trim().length < 3 ? "Name must be at least 3 characters" : ""}
          placeholder="Enter a descriptive name"
        />
      </div>
      
      <div className="upload-property">
        <RichTextEditor
          value={description}
          onChange={setDescription}
          label={isDescriptionRequired ? "Description *" : "Description"}
          placeholder="Enter a detailed description"
          toolbarConfig="description"
          minHeight="100px"
          error={
            isDescriptionRequired &&
            description.length > 0 &&
            description.trim().length < 10
          }
          helperText={
            isDescriptionRequired &&
            description.length > 0 &&
            description.trim().length < 10
              ? "Description must be at least 10 characters"
              : ""
          }
        />
      </div>

      {uploadError && (
        <div className="upload-property">
          <Alert severity="error" size="small">
            {uploadError}
          </Alert>
        </div>
      )}

      <div className="upload-property">
        {uploading ? (
          <div className="upload-progress">
            <CircularProgressWithLabel value={progress} />
            <span style={{ marginLeft: 8, fontSize: '12px' }}>Uploading...</span>
          </div>
        ) : uploadSucceeded ? (
          <div style={{ display: "flex", flexDirection: "column" }}>
            <div className="upload-success" style={{ display: "flex", alignItems: "center" }}>
              <CheckCircleIcon color="success" />
              <span style={{ marginLeft: 8, color: "green" }}>
                Upload completed!
              </span>
            </div>

            <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
              {onSelect && (
                <Button
                  variant="contained"
                  startIcon={<AssignmentTurnedInIcon />}
                  size="small"
                    onClick={() => {
                      const key = uploadFile.s3Key;
                      const cloudfrontDomain = "https://d1wve6lbxq3qje.cloudfront.net";
                      const url = key ? `${cloudfrontDomain}/${key}` : "";

                      onSelect({
                        ...uploadFile,
                        url,
                        urlDto: { imgUrl: url }
                      });
                    }}
                >
                  Select
                </Button>
              )}

              <Button
                className="file-remove-btn uploadSucceeded"
                variant="outlined"
                startIcon={<DeleteIcon />}
                onClick={() => removeUploadCardFromList(uploadFile.key)}
                size="small"
                color="error"
              >
                Remove
              </Button>
            </div>
          </div>
        ) : (
          <div className="actions">
            <Tooltip title={buttonStatus.tooltip}>
              <span>
                <Button
                  className={`file-upload-btn ${validated && isS3ConfigLoaded ? "validated" : ""}`}
                  variant={buttonStatus.variant}
                  endIcon={<FileUploadIcon />}
                  onClick={() => upload(uploadFile)}
                  disabled={buttonStatus.disabled}
                  size="small"
                >
                  Upload
                </Button>
              </span>
            </Tooltip>
            <Button
              className={`file-remove-btn ${uploadSucceeded ? "uploadSucceeded" : ""}`}
              variant="outlined"
              startIcon={<DeleteIcon />}
              onClick={() => removeUploadCardFromList(uploadFile.key)}
              size="small"
              color="error"
            >
              Remove
            </Button>
          </div>
        )}
      </div>
    </StyledFileUploadCard>
  ) : null
}

export default UploadItemCard
